import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Network, ShieldQuestion } from 'lucide-react';
import { GarantieReferentielDto } from 'api/guarantee';
import { BlockCard, StripedList } from 'back-office/shared/components/BlockCard';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { useBranches } from './useBranches';
import { UNASSIGNED_ROUTE_SEGMENT } from './branchesRoutes';
import { fetchAllGuarantees } from './fetchAllGuarantees';

/**
 * Liste des branches, avec le nombre de garanties que chacune porte.
 *
 * Le référentiel est parcouru une fois, page par page, et les compteurs s'en déduisent
 * côté client — plutôt qu'une requête par branche.
 */
export const BranchesList: FC = () => {
  const navigate = useNavigate();
  const branches = useBranches();

  const fetchGuarantees = useCallback(() => fetchAllGuarantees(), []);
  const { data, isLoading, error } = useAsyncResource(fetchGuarantees);

  const { countByBranch, unassignedCount } = useMemo(() => {
    const counts = new Map<string, number>();
    let orphans = 0;

    for (const guarantee of (data?.items ?? []) as GarantieReferentielDto[]) {
      if (!guarantee.branchId) {
        orphans += 1;
        continue;
      }
      counts.set(guarantee.branchId, (counts.get(guarantee.branchId) ?? 0) + 1);
    }
    return { countByBranch: counts, unassignedCount: orphans };
  }, [data]);

  return (
    <div className="flex flex-col gap-5">
      {/*
        La reprise de données a laissé sans branche les garanties qu'aucun tunnel
        n'utilisait. Sans une entrée visible, personne ne les retrouverait : elle n'est
        affichée que s'il en reste, et disparaîtra d'elle-même une fois le stock traité.
      */}
      {!isLoading && unassignedCount > 0 && (
        <button
          type="button"
          onClick={() => navigate(UNASSIGNED_ROUTE_SEGMENT)}
          className="flex items-center gap-3 rounded-2xl bg-amber-50 px-5 py-4 text-left shadow-sm ring-1 ring-amber-200 transition-colors hover:bg-amber-100"
        >
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-amber-900">
              {unassignedCount} garantie{unassignedCount > 1 ? 's' : ''} sans branche
            </span>
            <span className="block text-xs text-amber-800">
              Héritées de la reprise de données : à rattacher à une branche pour être
              proposées dans les tunnels.
            </span>
          </span>
          <ChevronRight className="h-4 w-4 flex-shrink-0 text-amber-600" />
        </button>
      )}

      <BlockCard
        title="Branches"
        description="Chaque branche porte le socle de garanties de ses tunnels"
        icon={<Network className="h-4 w-4" />}
        headerExtra={
          <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">
            {branches.length}
          </span>
        }
        isLoading={isLoading}
        error={error}
        isEmpty={branches.length === 0}
        emptyLabel="Aucune branche dans le référentiel"
        flushBody
      >
        <StripedList>
          {branches.map((branch) => {
            const count = countByBranch.get(branch.id) ?? 0;
            return (
              <li key={branch.id}>
                <button
                  type="button"
                  onClick={() => navigate(branch.id)}
                  className="flex w-full items-center gap-4 px-5 py-3 text-left"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-zinc-800">
                      {branch.label}
                    </span>
                    <span className="block truncate font-mono text-[11px] text-gray-400">
                      {branch.code}
                    </span>
                  </span>

                  <span
                    className={`flex flex-shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold ${
                      count > 0 ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    <ShieldQuestion className="h-3.5 w-3.5" />
                    {count} garantie{count > 1 ? 's' : ''}
                  </span>

                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300" />
                </button>
              </li>
            );
          })}
        </StripedList>
      </BlockCard>
    </div>
  );
};

export default BranchesList;
